import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, ChevronsRightLeft, ChevronsLeftRight, X, User, AlertTriangle } from 'lucide-react';
import { LeadsLayout } from '@/components/LeadsLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth header helper
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const STAGES = [
  { id: 'new', label: 'New Leads', color: 'bg-blue-500', bgLight: 'bg-blue-50', border: 'border-blue-200' },
  { id: 'contacted', label: 'Contacted', color: 'bg-purple-500', bgLight: 'bg-purple-50', border: 'border-purple-200' },
  { id: 'interested', label: 'Interested', color: 'bg-yellow-500', bgLight: 'bg-yellow-50', border: 'border-yellow-200' },
  { id: 'test_drive', label: 'Test Drive', color: 'bg-teal-500', bgLight: 'bg-teal-50', border: 'border-teal-200' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-pink-500', bgLight: 'bg-pink-50', border: 'border-pink-200' },
  { id: 'finance_pending', label: 'Finance Approval', color: 'bg-orange-500', bgLight: 'bg-orange-50', border: 'border-orange-200' },
  { id: 'dp_pending', label: 'DP Pending', color: 'bg-amber-500', bgLight: 'bg-amber-50', border: 'border-amber-200' },
  { id: 'won', label: 'Won 🏆', color: 'bg-green-500', bgLight: 'bg-green-50', border: 'border-green-200' },
  { id: 'lost', label: 'Lost', color: 'bg-red-500', bgLight: 'bg-red-50', border: 'border-red-200' }
];

// Defaults that mirror All Leads filters
const DEFAULT_OPEN_STAGES = STAGES.filter(s => s.id !== 'won' && s.id !== 'lost').map(s => s.id);
const ALL_PRIORITIES = ['hot', 'warm', 'cold'];
const COLLAPSE_KEY = 'kanban_collapsed_columns_v1';
const MY_PIPELINE_KEY = 'kanban_my_pipeline_only_v1';
const OVERDUE_KEY = 'kanban_overdue_only_v1';
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Shift a YYYY-MM-DD string by N days (positive = forward, negative = backward). Returns new YYYY-MM-DD string.
const shiftDate = (dateStr, days) => {
  if (!dateStr) return todayStr();
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const FINANCE_STATUS_OPTIONS = [
  { value: 'login', label: 'Login', short: 'LGN' },
  { value: 'fi_pending', label: 'FI Pending', short: 'FI' },
  { value: 'bank_statement_pending', label: 'Bank Statement Pending', short: 'BS' },
  { value: 'clarification_pending', label: 'Clarification Pending', short: 'CL' },
  { value: 'enach_pending', label: 'E-Nach Pending', short: 'EN' },
  { value: 'ipr_invoice_pending', label: 'IPR/Invoice Pending', short: 'IPR' },
  { value: 'disbursement_pending', label: 'Disbursement Pending', short: 'DP' },
  { value: 'disbursement_done', label: 'Disbursement Done', short: '✓' },
  { value: 'rejected', label: 'Rejected', short: '✗' }
];

const PRIORITY_COLORS = {
  hot: { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-100' },
  warm: { bg: 'bg-yellow-500', text: 'text-yellow-700', light: 'bg-yellow-100' },
  cold: { bg: 'bg-blue-500', text: 'text-blue-700', light: 'bg-blue-100' }
};

// Generate avatar color from name
const getAvatarColor = (name) => {
  const colors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
    'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-orange-500'
  ];
  const index = name ? name.charCodeAt(0) % colors.length : 0;
  return colors[index];
};

const getFinanceStatusLabel = (status) => {
  const option = FINANCE_STATUS_OPTIONS.find(o => o.value === status);
  return option ? option.short : '';
};

const getFinanceStatusColor = (status) => {
  if (status === 'disbursement_done') return 'bg-green-100 text-green-700';
  if (status === 'disbursement_pending') return 'bg-blue-100 text-blue-700';
  if (status === 'rejected') return 'bg-red-100 text-red-700';
  if (status === 'login') return 'bg-purple-100 text-purple-700';
  return 'bg-orange-100 text-orange-700';
};

// Date format helper - DD-MM-YYYY
const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

function LeadCard({ lead, onDragStart, onDragEnd, onClick, onFinanceStatusChange }) {
  const priorityStyle = PRIORITY_COLORS[lead.priority] || PRIORITY_COLORS.warm;
  const avatarColor = getAvatarColor(lead.first_name);
  const isWon = lead.stage === 'won';
  const isFinanceStage = lead.stage === 'finance_pending';
  const isDpPending = lead.stage === 'dp_pending';
  
  // Handle call
  const handleCall = (e) => {
    e.stopPropagation();
    window.open(`tel:${lead.mobile}`, '_self');
  };

  // Handle WhatsApp
  const handleWhatsApp = (e) => {
    e.stopPropagation();
    const message = encodeURIComponent(
      `Hi ${lead.first_name}, this is regarding your vehicle enquiry for ${lead.interested_model || 'our vehicles'}. Please let us know if you have any questions.`
    );
    { const _a = document.createElement('a'); _a.href = `https://web.whatsapp.com/send?phone=91${lead.mobile}&text=${message}`; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
  };
  
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onDragEnd={onDragEnd}
      onClick={() => onClick(lead)}
      className={`bg-white rounded-lg shadow-sm border p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-all group ${
        isWon ? 'border-green-300 ring-2 ring-green-200' : 'border-gray-200'
      }`}
      data-testid={`lead-card-${lead.id}`}
    >
      {/* Header with Avatar and Priority */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {/* Avatar */}
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${avatarColor} ${isWon ? 'ring-2 ring-green-400 ring-offset-1' : ''}`}>
            {lead.first_name?.[0]?.toUpperCase() || '?'}
            {lead.last_name?.[0]?.toUpperCase() || ''}
          </div>
          <div>
            <p className="font-semibold text-gray-800 text-sm group-hover:text-green-700">
              {lead.first_name} {lead.last_name || ''}
            </p>
            <p className="text-xs text-gray-500">#{lead.lead_no}</p>
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityStyle.light} ${priorityStyle.text}`}>
          {lead.priority === 'hot' ? '🔥' : lead.priority === 'warm' ? '🌡️' : '❄️'}
        </span>
      </div>
      
      {/* Vehicle Interest */}
      {lead.interested_model && (
        <div className="mb-2 px-2 py-1 bg-gray-50 rounded text-xs text-gray-600">
          🏍️ {lead.interested_brand && `${lead.interested_brand} `}{lead.interested_model}
          {lead.interested_variant && ` - ${lead.interested_variant}`}
        </div>
      )}
      
      {/* Financier Name - Show in Finance Approval stage */}
      {(isFinanceStage || isDpPending) && lead.preferred_financier && (
        <div className="mb-2 px-2 py-1 bg-orange-50 rounded text-xs text-orange-700 font-medium">
          🏦 {lead.preferred_financier}
        </div>
      )}
      
      {/* Finance Status - Show in Finance Approval stage */}
      {isFinanceStage && (
        <div className="mb-2" onClick={(e) => e.stopPropagation()}>
          <select
            value={lead.finance_status || 'fi_pending'}
            onChange={(e) => onFinanceStatusChange(lead, e.target.value)}
            className={`w-full px-2 py-1 rounded text-xs font-medium border-0 cursor-pointer ${getFinanceStatusColor(lead.finance_status)}`}
            data-testid={`finance-status-${lead.id}`}
          >
            {FINANCE_STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}
      
      {/* DP Pending badge */}
      {isDpPending && (
        <div className="mb-2 px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium text-center">
          💰 Down Payment Pending
        </div>
      )}
      
      {/* Contact Info with Action Buttons */}
      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
        <span>📱 {lead.mobile}</span>
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={handleCall}
            className="p-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            title="Call Customer"
          >
            📞
          </button>
          <button
            onClick={handleWhatsApp}
            className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
            title="WhatsApp"
          >
            💬
          </button>
        </div>
      </div>
      
      {/* Follow-up Date */}
      {lead.next_follow_up_date && (
        <div className={`text-xs px-2 py-1 rounded ${
          new Date(lead.next_follow_up_date) < new Date() 
            ? 'bg-red-100 text-red-700' 
            : 'bg-blue-100 text-blue-700'
        }`}>
          📅 {formatDateDDMMYYYY(lead.next_follow_up_date)}
          {lead.next_follow_up_time && ` at ${lead.next_follow_up_time}`}
        </div>
      )}
      
      {/* Won badge with special styling */}
      {isWon && (
        <div className="mt-2 flex items-center justify-center gap-1 bg-green-100 text-green-700 py-1 rounded text-xs font-medium">
          🏆 Converted to Booking
        </div>
      )}
      
      {/* Assigned Rep */}
      {lead.assigned_to && (
        <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
          👤 {lead.assigned_to}
        </div>
      )}
    </div>
  );
}

function KanbanColumn({ stage, leads, onDragOver, onDrop, onCardClick, onDragStart, onDragEnd, dragOverStage, onFinanceStatusChange, collapsed, onToggleCollapse }) {
  const stageLeads = leads.filter(l => l.stage === stage.id);
  const isDropTarget = dragOverStage === stage.id;
  
  // Collapsed mode — narrow vertical sliver showing only the title + count + expand chevron
  if (collapsed) {
    return (
      <div
        className={`flex-shrink-0 w-12 ${stage.bgLight} rounded-xl ${stage.border} border transition-all ${
          isDropTarget ? 'ring-2 ring-green-400 ring-offset-2' : ''
        }`}
        onDragOver={(e) => onDragOver(e, stage.id)}
        onDrop={(e) => onDrop(e, stage.id)}
        data-testid={`kanban-column-${stage.id}`}
        data-collapsed="true"
      >
        <button
          onClick={() => onToggleCollapse(stage.id)}
          className="w-full h-full flex flex-col items-center justify-between py-3 hover:bg-white/40 rounded-xl"
          title={`Expand ${stage.label}`}
          data-testid={`kanban-column-toggle-${stage.id}`}
        >
          <ChevronDown size={14} className="text-gray-500 rotate-[-90deg]" />
          <div className="flex-1 flex items-center justify-center">
            <span className="text-xs font-bold text-gray-700 whitespace-nowrap [writing-mode:vertical-rl] [transform:rotate(180deg)]">
              {stage.label}
            </span>
          </div>
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${stage.color} text-white`}>
            {stageLeads.length}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div 
      className={`flex-shrink-0 w-72 ${stage.bgLight} rounded-xl p-3 ${stage.border} border transition-all ${
        isDropTarget ? 'ring-2 ring-green-400 ring-offset-2' : ''
      }`}
      onDragOver={(e) => onDragOver(e, stage.id)}
      onDrop={(e) => onDrop(e, stage.id)}
      data-testid={`kanban-column-${stage.id}`}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between mb-3 px-2">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${stage.color}`}></div>
          <h3 className="font-bold text-gray-700 text-sm">{stage.label}</h3>
        </div>
        <div className="flex items-center gap-1">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stage.color} text-white`}>
            {stageLeads.length}
          </span>
          <button
            onClick={() => onToggleCollapse(stage.id)}
            className="p-1 text-gray-500 hover:text-gray-800 hover:bg-white/60 rounded transition-colors"
            title={`Collapse ${stage.label}`}
            data-testid={`kanban-column-toggle-${stage.id}`}
          >
            <ChevronUp size={14} />
          </button>
        </div>
      </div>
      
      {/* Cards */}
      <div className="space-y-3 min-h-[200px] max-h-[calc(100vh-280px)] overflow-y-auto">
        {stageLeads.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            No leads in this stage
          </div>
        ) : (
          stageLeads.map(lead => (
            <LeadCard
              key={lead.id}
              lead={lead}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onClick={onCardClick}
              onFinanceStatusChange={onFinanceStatusChange}
            />
          ))
        )}
      </div>
    </div>
  );
}

function KanbanBoard({ user, onLogout }) {
  const navigate = useNavigate();
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [draggedLead, setDraggedLead] = React.useState(null);
  const [dragOverStage, setDragOverStage] = React.useState(null);
  const [filters, setFilters] = React.useState({
    priority: '',
    assigned_to: '',
    search: '',
    created_from: todayStr(),
    created_to: todayStr(),
    followup_from: todayStr(),
    followup_to: todayStr(),
  });
  const [salesReps, setSalesReps] = React.useState([]);

  // Collapsed columns state — persisted in localStorage so user preference survives reloads
  const [collapsedColumns, setCollapsedColumns] = React.useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const handleToggleCollapse = React.useCallback((stageId) => {
    setCollapsedColumns(prev => {
      const next = prev.includes(stageId) ? prev.filter(s => s !== stageId) : [...prev, stageId];
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch { /* ignore quota */ }
      return next;
    });
  }, []);

  const handleCollapseAll = React.useCallback(() => {
    const all = STAGES.map(s => s.id);
    setCollapsedColumns(all);
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(all)); } catch { /* ignore quota */ }
  }, []);

  const handleExpandAll = React.useCallback(() => {
    setCollapsedColumns([]);
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([])); } catch { /* ignore quota */ }
  }, []);

  // "Show only my pipeline" toggle — persisted; matches lead.assigned_to against current user's name
  const [myPipelineOnly, setMyPipelineOnly] = React.useState(() => {
    try {
      return localStorage.getItem(MY_PIPELINE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const myName = user?.employee_name || user?.name || '';

  const toggleMyPipeline = React.useCallback(() => {
    setMyPipelineOnly(prev => {
      const next = !prev;
      try { localStorage.setItem(MY_PIPELINE_KEY, next ? '1' : '0'); } catch { /* ignore quota */ }
      return next;
    });
  }, []);

  // "Overdue follow-ups only" toggle — persisted; shows leads with next_follow_up_date < today AND stage not in (won, lost)
  const [overdueOnly, setOverdueOnly] = React.useState(() => {
    try {
      return localStorage.getItem(OVERDUE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleOverdueOnly = React.useCallback(() => {
    setOverdueOnly(prev => {
      const next = !prev;
      try { localStorage.setItem(OVERDUE_KEY, next ? '1' : '0'); } catch { /* ignore quota */ }
      return next;
    });
  }, []);

  React.useEffect(() => {
    fetchLeads();
    fetchSalesReps();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads?limit=500`, getAuthHeader());
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    }
    setLoading(false);
  };

  const fetchSalesReps = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters?type=sales_rep`, getAuthHeader());
      setSalesReps(response.data);
    } catch (error) {
      console.error('Error fetching sales reps:', error);
    }
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedLead(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageId);
  };

  // Handle finance status change
  const handleFinanceStatusChange = async (lead, newStatus) => {
    // Optimistic update
    setLeads(prev => prev.map(l => 
      l.id === lead.id ? { ...l, finance_status: newStatus } : l
    ));
    
    try {
      await axios.put(`${BACKEND_URL}/api/leads/${lead.id}/finance-status/${newStatus}`, {}, getAuthHeader());
      toast.success(`Finance status updated to ${FINANCE_STATUS_OPTIONS.find(o => o.value === newStatus)?.label}`);
      
      // If disbursement is done, prompt to create booking
      if (newStatus === 'disbursement_done') {
        const shouldConvert = window.confirm('Disbursement is complete! Create a new booking in Sales Module?');
        if (shouldConvert) {
          try {
            const response = await axios.post(`${BACKEND_URL}/api/leads/${lead.id}/convert`, {}, getAuthHeader());
            toast.success(`Booking #${response.data.booking?.booking_no} created!`);
            fetchLeads(); // Refresh to get updated data
          } catch (convError) {
            console.error('Conversion error:', convError);
            toast.error('Finance status updated, but booking creation failed');
          }
        }
      }
    } catch (error) {
      // Revert on error
      setLeads(prev => prev.map(l => 
        l.id === lead.id ? { ...l, finance_status: lead.finance_status } : l
      ));
      console.error('Error updating finance status:', error);
      toast.error('Failed to update finance status');
    }
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    setDragOverStage(null);
    
    if (!draggedLead || draggedLead.stage === newStage) {
      setDraggedLead(null);
      return;
    }
    
    // Optimistic update
    const oldStage = draggedLead.stage;
    setLeads(prev => prev.map(l => 
      l.id === draggedLead.id ? { ...l, stage: newStage } : l
    ));
    
    try {
      await axios.put(`${BACKEND_URL}/api/leads/${draggedLead.id}/stage/${newStage}`, {}, getAuthHeader());
      toast.success(`Lead moved to ${STAGES.find(s => s.id === newStage)?.label}`);
      
      // If moved to won, ask about conversion
      if (newStage === 'won') {
        const shouldConvert = window.confirm('Would you like to convert this lead to a booking?');
        if (shouldConvert) {
          try {
            const response = await axios.post(`${BACKEND_URL}/api/leads/${draggedLead.id}/convert`, {}, getAuthHeader());
            toast.success(`Booking #${response.data.booking?.booking_no} created!`);
            fetchLeads(); // Refresh to get updated data
          } catch (convError) {
            console.error('Conversion error:', convError);
            toast.error('Lead marked as won, but booking conversion failed');
          }
        }
      }
    } catch (error) {
      // Revert on error
      setLeads(prev => prev.map(l => 
        l.id === draggedLead.id ? { ...l, stage: oldStage } : l
      ));
      console.error('Error updating lead stage:', error);
      toast.error('Failed to update lead stage');
    }
    
    setDraggedLead(null);
  };

  const handleCardClick = (lead) => {
    navigate(`/leads/view/${lead.id}`);
  };

  // Today's overdue follow-ups — leads with next_follow_up_date < today AND stage not in (won, lost).
  // Computed from UNFILTERED leads so the badge count is the true "what reps are behind on".
  const overdueLeadIds = React.useMemo(() => {
    const today = todayStr();
    const ids = new Set();
    for (const l of leads) {
      if (!l.next_follow_up_date) continue;
      if (l.next_follow_up_date >= today) continue;
      if (l.stage === 'won' || l.stage === 'lost') continue;
      ids.add(l.id);
    }
    return ids;
  }, [leads]);

  // Filter leads
  const filteredLeads = React.useMemo(() => {
    return leads.filter(lead => {
      if (overdueOnly && !overdueLeadIds.has(lead.id)) return false;
      if (myPipelineOnly && myName && lead.assigned_to !== myName) return false;
      if (filters.priority && lead.priority !== filters.priority) return false;
      if (filters.assigned_to && lead.assigned_to !== filters.assigned_to) return false;
      if (filters.search) {
        const search = filters.search.toLowerCase();
        const matchName = `${lead.first_name} ${lead.last_name}`.toLowerCase().includes(search);
        const matchMobile = lead.mobile?.includes(search);
        const matchModel = lead.interested_model?.toLowerCase().includes(search);
        if (!matchName && !matchMobile && !matchModel) return false;
      }
      // Created date range filter
      if (filters.created_from || filters.created_to) {
        const leadDate = lead.created_at ? lead.created_at.split('T')[0] : '';
        if (filters.created_from && (!leadDate || leadDate < filters.created_from)) return false;
        if (filters.created_to && (!leadDate || leadDate > filters.created_to)) return false;
      }
      // Follow-up date range filter — leads with no follow-up date pass through (option b)
      if (filters.followup_from || filters.followup_to) {
        const fup = lead.next_follow_up_date || '';
        if (fup) {
          if (filters.followup_from && fup < filters.followup_from) return false;
          if (filters.followup_to && fup > filters.followup_to) return false;
        }
      }
      return true;
    });
  }, [leads, filters, myPipelineOnly, myName, overdueOnly, overdueLeadIds]);

  // Calculate stage counts
  const stageCounts = React.useMemo(() => {
    return STAGES.reduce((acc, stage) => {
      acc[stage.id] = filteredLeads.filter(l => l.stage === stage.id).length;
      return acc;
    }, {});
  }, [filteredLeads]);

  if (loading) {
    return (
      <LeadsLayout user={user} onLogout={onLogout} title="Kanban Board">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </LeadsLayout>
    );
  }

  return (
    <LeadsLayout user={user} onLogout={onLogout} title="Kanban Board">
      <div className="space-y-4">
        {/* Top Stats Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-2xl font-bold text-gray-800">{filteredLeads.length}</p>
                <p className="text-xs text-gray-500">Total Leads</p>
              </div>
              <div className="h-10 w-px bg-gray-200"></div>
              <div className="flex gap-4">
                {STAGES.slice(0, 5).map(stage => (
                  <div key={stage.id} className="text-center">
                    <p className="text-lg font-bold" style={{ color: stage.color.replace('bg-', 'rgb(').replace('-500', ')') }}>
                      {stageCounts[stage.id] || 0}
                    </p>
                    <p className="text-xs text-gray-500">{stage.label}</p>
                  </div>
                ))}
              </div>
              {overdueLeadIds.size > 0 && (
                <>
                  <div className="h-10 w-px bg-gray-200"></div>
                  <button
                    onClick={toggleOverdueOnly}
                    className={`group flex items-center gap-2 px-3 py-2 rounded-xl border transition-all duration-180 ease-out hover:scale-[1.03] active:scale-95 ${
                      overdueOnly
                        ? 'bg-rose-600 border-rose-700 text-white shadow-[0_4px_14px_rgba(225,29,72,0.45)] animate-pulse'
                        : 'bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100 hover:border-rose-300 shadow-[0_2px_8px_rgba(225,29,72,0.15)]'
                    }`}
                    title={overdueOnly ? 'Showing only overdue follow-ups — click to show all' : `${overdueLeadIds.size} lead${overdueLeadIds.size > 1 ? 's' : ''} have overdue follow-ups — click to filter`}
                    data-testid="kanban-overdue-badge"
                    aria-pressed={overdueOnly}
                  >
                    <AlertTriangle className={`w-4 h-4 ${overdueOnly ? 'text-white' : 'text-rose-600'}`} />
                    <div className="flex flex-col items-start leading-tight">
                      <span className={`text-lg font-bold ${overdueOnly ? 'text-white' : 'text-rose-700'}`} data-testid="kanban-overdue-count">
                        {overdueLeadIds.size}
                      </span>
                      <span className={`text-[10px] font-semibold uppercase tracking-wide ${overdueOnly ? 'text-rose-100' : 'text-rose-600'}`}>
                        {overdueOnly ? 'Showing Overdue' : "Today's Overdue"}
                      </span>
                    </div>
                  </button>
                </>
              )}
            </div>
            
            {/* Quick Actions */}
            <div className="flex items-center gap-3">
              {myName && (
                <button
                  onClick={toggleMyPipeline}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg transition-colors ${
                    myPipelineOnly
                      ? 'text-white bg-emerald-600 hover:bg-emerald-700 shadow-[0_2px_8px_rgba(5,150,105,0.30)]'
                      : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                  }`}
                  title={myPipelineOnly ? `Showing only ${myName}'s leads — click to show all` : `Show only ${myName}'s leads`}
                  data-testid="kanban-my-pipeline-toggle"
                  aria-pressed={myPipelineOnly}
                >
                  <User className="w-3.5 h-3.5" />
                  {myPipelineOnly ? `Only ${myName}` : 'My Pipeline'}
                </button>
              )}
              {collapsedColumns.length < STAGES.length && (
                <button
                  onClick={handleCollapseAll}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Collapse all columns"
                  data-testid="kanban-collapse-all"
                >
                  <ChevronsRightLeft className="w-3.5 h-3.5" />
                  Collapse All
                </button>
              )}
              {collapsedColumns.length > 0 && (
                <button
                  onClick={handleExpandAll}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                  title="Expand all columns"
                  data-testid="kanban-expand-all"
                >
                  <ChevronsLeftRight className="w-3.5 h-3.5" />
                  Expand All
                </button>
              )}
              <button
                onClick={() => navigate('/leads/add')}
                className="px-4 py-2 text-white rounded-xl text-sm font-medium shadow-[0_4px_12px_rgba(5,150,105,0.30)] hover:shadow-[0_8px_20px_rgba(5,150,105,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
                style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' }}
                data-testid="add-lead-btn"
              >
                + Add Lead
              </button>
              <button
                onClick={fetchLeads}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                title="Refresh"
              >
                🔄
              </button>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Filter:</span>
              <input
                type="text"
                placeholder="Search name, mobile, model..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 w-56"
                data-testid="kanban-search-input"
              />
            </div>
            
            <select
              value={filters.priority}
              onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              data-testid="kanban-priority-filter"
            >
              <option value="">All Priorities</option>
              <option value="hot">🔥 Hot</option>
              <option value="warm">🌡️ Warm</option>
              <option value="cold">❄️ Cold</option>
            </select>
            
            <select
              value={filters.assigned_to}
              onChange={(e) => setFilters(prev => ({ ...prev, assigned_to: e.target.value }))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              data-testid="kanban-rep-filter"
            >
              <option value="">All Sales Reps</option>
              {salesReps.map(rep => (
                <option key={rep.id} value={rep.name}>{rep.name}</option>
              ))}
            </select>

            <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-lg border border-blue-200" data-testid="kanban-created-date-filter">
              <span className="text-xs font-medium text-blue-700 whitespace-nowrap">📅 Created:</span>
              <input
                type="date"
                value={filters.created_from}
                onChange={(e) => setFilters(prev => ({ ...prev, created_from: e.target.value }))}
                className="px-2 py-0.5 border-0 bg-transparent focus:ring-0 text-sm"
                data-testid="kanban-created-from"
              />
              <span className="text-blue-400">to</span>
              <input
                type="date"
                value={filters.created_to}
                onChange={(e) => setFilters(prev => ({ ...prev, created_to: e.target.value }))}
                className="px-2 py-0.5 border-0 bg-transparent focus:ring-0 text-sm"
                data-testid="kanban-created-to"
              />
              <div className="flex items-center gap-0.5 ml-1 border-l border-blue-200 pl-1.5">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, created_from: shiftDate(prev.created_from, -1), created_to: shiftDate(prev.created_to, -1) }))}
                  className="px-1.5 py-0.5 text-[11px] text-blue-700 bg-white hover:bg-blue-100 rounded transition-colors"
                  title="Shift both dates back by 1 day"
                  data-testid="kanban-created-prev"
                >
                  ◀ Prev
                </button>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, created_from: todayStr(), created_to: todayStr() }))}
                  className="px-1.5 py-0.5 text-[11px] text-blue-700 bg-white hover:bg-blue-100 rounded transition-colors"
                  title="Set both dates to today"
                  data-testid="kanban-created-today"
                >
                  Today
                </button>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, created_from: shiftDate(prev.created_from, 1), created_to: shiftDate(prev.created_to, 1) }))}
                  className="px-1.5 py-0.5 text-[11px] text-blue-700 bg-white hover:bg-blue-100 rounded transition-colors"
                  title="Shift both dates forward by 1 day"
                  data-testid="kanban-created-next"
                >
                  Next ▶
                </button>
              </div>
              {(filters.created_from || filters.created_to) && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, created_from: '', created_to: '' }))}
                  className="text-red-500 hover:text-red-700 px-1"
                  title="Clear created date filter"
                  data-testid="kanban-created-clear"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-lg border border-amber-200" data-testid="kanban-followup-date-filter">
              <span className="text-xs font-medium text-amber-700 whitespace-nowrap">🔔 Follow-up:</span>
              <input
                type="date"
                value={filters.followup_from}
                onChange={(e) => setFilters(prev => ({ ...prev, followup_from: e.target.value }))}
                className="px-2 py-0.5 border-0 bg-transparent focus:ring-0 text-sm"
                data-testid="kanban-followup-from"
              />
              <span className="text-amber-400">to</span>
              <input
                type="date"
                value={filters.followup_to}
                onChange={(e) => setFilters(prev => ({ ...prev, followup_to: e.target.value }))}
                className="px-2 py-0.5 border-0 bg-transparent focus:ring-0 text-sm"
                data-testid="kanban-followup-to"
              />
              <div className="flex items-center gap-0.5 ml-1 border-l border-amber-200 pl-1.5">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, followup_from: shiftDate(prev.followup_from, -1), followup_to: shiftDate(prev.followup_to, -1) }))}
                  className="px-1.5 py-0.5 text-[11px] text-amber-700 bg-white hover:bg-amber-100 rounded transition-colors"
                  title="Shift both dates back by 1 day"
                  data-testid="kanban-followup-prev"
                >
                  ◀ Prev
                </button>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, followup_from: todayStr(), followup_to: todayStr() }))}
                  className="px-1.5 py-0.5 text-[11px] text-amber-700 bg-white hover:bg-amber-100 rounded transition-colors"
                  title="Set both dates to today"
                  data-testid="kanban-followup-today"
                >
                  Today
                </button>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, followup_from: shiftDate(prev.followup_from, 1), followup_to: shiftDate(prev.followup_to, 1) }))}
                  className="px-1.5 py-0.5 text-[11px] text-amber-700 bg-white hover:bg-amber-100 rounded transition-colors"
                  title="Shift both dates forward by 1 day"
                  data-testid="kanban-followup-next"
                >
                  Next ▶
                </button>
              </div>
              {(filters.followup_from || filters.followup_to) && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, followup_from: '', followup_to: '' }))}
                  className="text-red-500 hover:text-red-700 px-1"
                  title="Clear follow-up date filter"
                  data-testid="kanban-followup-clear"
                >
                  ✕
                </button>
              )}
            </div>
            
            {(filters.search || filters.priority || filters.assigned_to || filters.created_from || filters.created_to || filters.followup_from || filters.followup_to) && (
              <button
                onClick={() => setFilters({ priority: '', assigned_to: '', search: '', created_from: '', created_to: '', followup_from: '', followup_to: '' })}
                className="text-sm text-red-600 hover:underline"
                data-testid="kanban-clear-all-filters"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Kanban Board */}
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map(stage => (
            <KanbanColumn
              key={stage.id}
              stage={stage}
              leads={filteredLeads}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onCardClick={handleCardClick}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              dragOverStage={dragOverStage}
              onFinanceStatusChange={handleFinanceStatusChange}
              collapsed={collapsedColumns.includes(stage.id)}
              onToggleCollapse={handleToggleCollapse}
            />
          ))}
        </div>

        {/* Drag Hint */}
        <p className="text-center text-xs text-gray-400">
          💡 Drag and drop cards between columns to update lead stage
        </p>
      </div>
    </LeadsLayout>
  );
}

export default KanbanBoard;
